import { CloseButton, Menu, Table, Text, Tooltip } from '@mantine/core';
import {
	IconChevronDown,
	IconChevronUp,
	IconCircleCheck,
	IconPencil,
	IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { KeyedRow } from '../../../components/KeyedRow';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { formatToDateStringOrDefault } from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import { usePrescriptionAuth } from '../hooks/usePrescriptionAuth';
import type {
	PrescriptionDto,
	UpsertPrescriptionDto,
} from '../types/PrescriptionDto';
import { EditPrescriptionModal } from './EditPrescriptionModal';
import { VerifyPrescriptionModal } from './VerifyPrescriptionModal';

interface PrescriptionRowProps {
	initialPrescription: PrescriptionDto;
	refreshTable: () => void;
}

export function PrescriptionRow({
	initialPrescription,
	refreshTable,
}: PrescriptionRowProps) {
	const { organisation } = useParams();

	const [prescription, setPrescription] = useState(initialPrescription);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const editModal = useModal();
	const verifyModal = useModal();
	const deleteModal = useModal();

	const displayDate = formatToDateStringOrDefault(prescription.dateTime);

	const deleteMutation = mutate<PrescriptionDto>(
		`${organisation}/prescriptions/delete/${prescription.pkid}`,
	);

	const { authMessage, verifiedMessage } = usePrescriptionAuth(prescription);

	return (
		<>
			<EditPrescriptionModal
				close={editModal.close}
				opened={editModal.isOpen}
				updateTableValue={setPrescription}
				initialPrescription={toUpsertPrescription(prescription)}
			/>
			<VerifyPrescriptionModal
				close={verifyModal.close}
				isOpened={verifyModal.isOpen}
				updateTableRow={setPrescription}
				displayDate={displayDate}
				prescriptionId={prescription.pkid}
			/>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Prescription'}
				itemDescription={`${prescription.branch} (${displayDate})`}
				mutation={deleteMutation}
				onComplete={refreshTable}
			/>
			<KeyedRow key={prescription.pkid}>
				<Table.Td ta={centerIfPresent(prescription.dateTime)}>
					{formatToDateStringOrDefault(prescription.dateTime)}
				</Table.Td>
				<Table.Td ta={centerIfPresent(prescription.branch)}>
					{undefinedCheck(prescription.branch)}
				</Table.Td>
				<Table.Td ta={centerIfPresent(prescription.forms)}>
					{undefinedCheck(prescription.forms?.toString())}
				</Table.Td>
				<Table.Td ta={centerIfPresent(prescription.items)}>
					{undefinedCheck(prescription.items?.toString())}
				</Table.Td>
				<Table.Td ta={centerIfPresent(prescription.dailyInstallments)}>
					{undefinedCheck(prescription.dailyInstallments?.toString())}
				</Table.Td>
				<Table.Td ta={centerIfPresent(prescription.weeklyInstallments)}>
					{undefinedCheck(prescription.weeklyInstallments?.toString())}
				</Table.Td>
				<Table.Td ta={centerIfPresent(prescription.methadone)}>
					{undefinedCheck(prescription.methadone?.toString())}
				</Table.Td>
				<Tooltip
					label={undefinedCheck(prescription.verifiedBy)}
					disabled={(prescription.verifiedBy?.length ?? 0) < 30}
				>
					<Table.Td>
						<Text
							w={200}
							truncate={'end'}
							ta={centerIfPresent(prescription.verifiedBy)}
						>
							{undefinedCheck(prescription.verifiedBy)}
						</Text>
					</Table.Td>
				</Tooltip>
				<Table.Td>
					<CloseButton
						icon={
							!isDropdownOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setIsDropdownOpen(!isDropdownOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Tooltip label={authMessage} hidden={authMessage === undefined}>
							<Menu.Item
								leftSection={<IconPencil {...defaultIconStyle} />}
								onClick={() => editModal.open()}
								data-cy={'edit-button'}
								disabled={authMessage !== undefined}
							>
								Edit
							</Menu.Item>
						</Tooltip>
						<Tooltip
							label={authMessage ?? verifiedMessage}
							hidden={
								authMessage === undefined && verifiedMessage === undefined
							}
						>
							<Menu.Item
								leftSection={<IconCircleCheck {...defaultIconStyle} />}
								onClick={() => verifyModal.open()}
								data-cy={'verify-button'}
								disabled={
									authMessage !== undefined || verifiedMessage !== undefined
								}
							>
								Verify
							</Menu.Item>
						</Tooltip>
						<Tooltip label={authMessage} hidden={authMessage === undefined}>
							<Menu.Item
								leftSection={<IconTrash {...defaultIconStyle} />}
								c={'red'}
								onClick={() => deleteModal.open()}
								data-cy={'delete-button'}
								disabled={authMessage !== undefined}
							>
								Delete
							</Menu.Item>
						</Tooltip>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={isDropdownOpen}>
				<Table withRowBorders pt={0} mt={0}>
					<Table.Thead>
						<KeyedRow>
							<Table.Th>PFS Forms</Table.Th>
							<Table.Th>PFS Items</Table.Th>
							<Table.Th>PFS Referrals</Table.Th>
							<Table.Th>PFS Advice</Table.Th>
							<Table.Th>Serial Rx Items</Table.Th>
							<Table.Th>GP10a Forms</Table.Th>
							<Table.Th>GP10a Items</Table.Th>
							<Table.Th>Other CP4</Table.Th>
						</KeyedRow>
					</Table.Thead>
					<Table.Tbody>
						<KeyedRow>
							<Table.Td ta={centerIfPresent(prescription.pfsForms)}>
								{undefinedCheck(prescription.pfsForms?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.pfsItems)}>
								{undefinedCheck(prescription.pfsItems?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.pgsReferrals)}>
								{undefinedCheck(prescription.pgsReferrals?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.pfsConsultations)}>
								{undefinedCheck(prescription.pfsConsultations?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.serialRxItems)}>
								{undefinedCheck(prescription.serialRxItems?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.gp10aForms)}>
								{undefinedCheck(prescription.gp10aForms?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.gp10aItems)}>
								{undefinedCheck(prescription.gp10aItems?.toString())}
							</Table.Td>
							<Table.Td ta={centerIfPresent(prescription.otherCp4)}>
								{undefinedCheck(prescription.otherCp4?.toString())}
							</Table.Td>
						</KeyedRow>
					</Table.Tbody>
				</Table>
			</TableRowCollapsible>
		</>
	);
}

function centerIfPresent(
	value: number | string | Date | undefined | null,
): 'center' | undefined {
	return value !== undefined && value !== null && value.toString().trim() !== ''
		? 'center'
		: undefined;
}

function toUpsertPrescription(
	prescription: PrescriptionDto,
): UpsertPrescriptionDto {
	return {
		pkid: prescription.pkid,
		branchId: prescription.branchId?.toString() ?? '',
		dateTime: prescription.dateTime
			? new Date(prescription.dateTime)
			: undefined,
		forms: prescription.forms,
		items: prescription.items,
		dailyInstallments: prescription.dailyInstallments,
		weeklyInstallments: prescription.weeklyInstallments,
		pfsForms: prescription.pfsForms,
		pfsItems: prescription.pfsItems,
		pgsReferrals: prescription.pgsReferrals,
		pfsConsultations: prescription.pfsConsultations,
		serialRxItems: prescription.serialRxItems,
		gp10aForms: prescription.gp10aForms,
		gp10aItems: prescription.gp10aItems,
		otherCp4: prescription.otherCp4,
		methadone: prescription.methadone,
	};
}
